import { createContext, useContext, useState, useCallback } from 'react';

const I18nContext = createContext();

const translations = {
  en: {
    // Layout
    'nav.dashboard': 'Dashboard',
    'nav.stockAnalysis': 'Stock Analysis',
    'nav.recommendations': 'Recommendations',
    'nav.logout': 'Logout',
    'nav.login': 'Login',
    'nav.loginHint': 'Higher rate limits',

    // Login
    'login.title': 'Sorane',
    'login.subtitle': 'AI-Powered Trading Platform',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.submit': 'Sign In',
    'login.loading': 'Signing in...',
    'login.trustDevice': 'Trust this device (stay signed in for 30 days)',
    'login.noAccount': "Don't have an account?",
    'login.register': 'Register',
    'login.continueAsGuest': 'Continue as guest (limited access)',

    // Register
    'register.title': 'Create Account',
    'register.subtitle': 'Join Sorane Trading Platform',
    'register.name': 'Name',
    'register.email': 'Email',
    'register.password': 'Password',
    'register.confirmPassword': 'Confirm Password',
    'register.submit': 'Register',
    'register.loading': 'Creating account...',
    'register.hasAccount': 'Already have an account?',
    'register.signIn': 'Sign In',
    'register.passwordMismatch': 'Passwords do not match',
    'register.passwordTooShort': 'Password must be at least 6 characters',
    'register.codeSent': 'A verification code has been sent to your email.',
    'register.code': 'Verification Code',
    'register.verify': 'Verify & Create Account',
    'register.verifying': 'Verifying...',
    'register.resend': 'Resend Code',
    'register.backToForm': 'Back',

    // Dashboard
    'dash.title': 'Dashboard',
    'dash.subtitle': 'AI-powered stock analysis and trading recommendations',
    'dash.stockAnalysis': 'Stock Analysis',
    'dash.stockAnalysisDesc': 'Deep-dive evaluation with scoring system',
    'dash.recommendations': 'Recommendations',
    'dash.recommendationsDesc': 'Monthly stock screening (US & CN)',
    'dash.contactTitle': 'Contact Us',
    'dash.contactBody': 'If you have any questions or ideas, please send a message to',
    'dash.version': 'Version',
    'dash.placeholder': 'Enter tickers (e.g. AAPL, NVDA, SH600000)',
    'dash.evaluate': 'Evaluate',
    'dash.analyzing': 'Analyzing...',
    'dash.quick': 'Quick:',

    // Market Emotion
    'emotion.title': 'Market Emotion',
    'emotion.usMarket': 'US Market',
    'emotion.cnMarket': 'CN Market',
    'emotion.score': 'Score',
    'emotion.momentum': 'Momentum',
    'emotion.breadth': 'Breadth',
    'emotion.volatility': 'Volatility',
    'emotion.safeHaven': 'Safe Haven',
    'emotion.junkSpread': 'Junk Bond Spread',
    'emotion.rsi': 'Index RSI',
    'emotion.highLow': '52W High/Low',
    'emotion.loading': 'Reading market pulse...',
    'emotion.error': 'Could not load market emotion',

    // Stock Detail
    'stock.title': 'Stock Analysis',
    'stock.subtitle': 'Rule-based stock scoring with price history and financials',
    'stock.placeholder': 'Enter ticker (e.g. AAPL, NVDA, SH600000)',
    'stock.analyze': 'Analyze',
    'stock.analyzing': 'Analyzing...',
    'stock.quick': 'Quick:',
    'stock.target': 'Target',
    'stock.overallScore': 'Overall Score',
    'stock.tabScores': 'Score Breakdown',
    'stock.tabChart': 'Price Chart',
    'stock.tabFinancials': 'Financials',
    'stock.scoreDimensions': 'Scoring Dimensions',
    'stock.keyMetrics': 'Key Metrics',
    'stock.currentPrice': 'Current Price',
    'stock.targetPrice': 'Target Price',
    'stock.upside': 'Upside',
    'stock.confidence': 'Confidence',
    'stock.sector': 'Sector',
    'stock.capCategory': 'Cap Category',
    'stock.strengths': 'Strengths',
    'stock.risks': 'Risks',
    'stock.analysisSummary': 'Analysis Summary',
    'stock.fiveDayTrend': '5-Day Trend',
    'stock.oneMonthTrend': '1-Month Trend',
    'stock.threeMonthTrend': '3-Month Trend',
    'stock.peValuation': 'P/E Valuation',
    'stock.dividendYield': 'Dividend Yield',
    'stock.betaRisk': 'Beta (Risk)',
    'stock.marketCap': 'Market Cap',
    'stock.volAdjReturn': 'Risk-Adjusted Return',
    'stock.rsiSignal': 'RSI Signal',
    'stock.earningsGrowth': 'Earnings Growth',
    'stock.financialHealth': 'Financial Health',
    'stock.sectorMomentum': 'Sector Momentum',
    'stock.noPriceData': 'No price data available',
    'stock.noFinancialData': 'No financial data available',
    'stock.invalidTicker': 'Please enter a valid stock symbol (e.g. AAPL) or CN stock code (e.g. SH600519, 002713). Chinese stock names are not supported.',

    // Financials
    'fin.price': 'Price',
    'fin.from52wHigh': 'from 52w high',
    'fin.ytdReturn': 'YTD Return',
    'fin.peRatio': 'P/E Ratio',
    'fin.financialHealth': 'Financial Health',
    'fin.strong': 'Strong',
    'fin.moderate': 'Moderate',
    'fin.weak': 'Weak',
    'fin.valuation': 'Valuation',
    'fin.earningsProfitability': 'Earnings & Profitability',
    'fin.latestEps': 'Latest Quarterly EPS',
    'fin.epsGrowth': 'EPS Growth (QoQ)',
    'fin.latestRevenue': 'Latest Revenue',
    'fin.profitMargin': 'Profit Margin',
    'fin.freeCashFlow': 'Free Cash Flow',
    'fin.currentRatio': 'Current Ratio',
    'fin.debtAssets': 'Debt / Assets',
    'fin.trailingPE': 'Trailing P/E',
    'fin.forwardPE': 'Forward P/E',
    'fin.priceBook': 'Price/Book',
    'fin.pegRatio': 'PEG Ratio',
    'fin.evEbitda': 'EV/EBITDA',
    'fin.weekRange': '52-Week Range',
    'fin.perfVsMarket': 'Performance vs Market',

    // Recommendations
    'rec.title': 'Stock Recommendations',
    'rec.subtitle': 'Trend-based stock screening across US and Chinese markets',
    'rec.usMarket': '🇺🇸 US Market',
    'rec.cnMarket': '🇨🇳 China Market',
    'rec.generate': 'Generate Recommendations',
    'rec.generating': 'Generating...',
    'rec.marketHealth': '📊 Overall Market Health',
    'rec.stocksScored': 'Stocks Scored',
    'rec.buySignals': 'Buy Signals',
    'rec.sectorsWithPicks': 'Sectors w/ Picks',
    'rec.benchmarkRsi': 'Benchmark RSI',
    'rec.topSectors': 'Top Performing Sectors',
    'rec.topPicks': 'Top Picks',
    'rec.top': 'Top',
    'rec.ticker': 'Ticker',
    'rec.signal': 'Signal',
    'rec.score': 'Score',
    'rec.price': 'Price',
    'rec.ret1m': '1M Return',
    'rec.rsi': 'RSI',
    'rec.sector': 'Sector',


    // Landing Page - Nav
    'landing.nav.features': 'Features',
    'landing.nav.testimonials': 'Testimonials',
    'landing.nav.signup': 'Sign Up',
    'landing.nav.login': 'Login',

    // Landing Page - Hero
    'landing.hero.title': 'AI-Powered Trading Analysis',
    'landing.hero.subtitle': 'Smart stock scoring, custom strategies, and real-time market insights — all in one platform.',
    'landing.hero.getStarted': 'Get Started',
    'landing.hero.login': 'Login',

    // Landing Page - Features
    'landing.features.title': 'Everything You Need to Trade Smarter',
    'landing.features.stockAnalysis.title': 'Stock Analysis',
    'landing.features.stockAnalysis.desc': 'Deep-dive evaluation with multi-dimensional scoring across fundamentals and technicals.',
    'landing.features.aiRecommendations.title': 'AI Recommendations',
    'landing.features.aiRecommendations.desc': 'Monthly stock screening across US and Chinese markets powered by rule-based scoring.',
    'landing.features.marketEmotion.title': 'Market Emotion',
    'landing.features.marketEmotion.desc': 'Real-time Fear & Greed index from 7 market signals for US and Chinese markets.',

    // Landing Page - Testimonials
    'landing.testimonials.title': 'Trusted by Traders',
    'landing.testimonials.1.quote': 'Sorane\'s scoring system helped me identify undervalued stocks I would have missed.',
    'landing.testimonials.1.name': 'Alex T.',
    'landing.testimonials.1.role': 'Retail Investor',
    'landing.testimonials.2.quote': 'The Strategy Lab lets me test my ideas against real historical data before risking capital.',
    'landing.testimonials.2.name': 'Sarah L.',
    'landing.testimonials.2.role': 'Swing Trader',
    'landing.testimonials.3.quote': 'Having both US and Chinese market coverage in one platform is a game changer.',
    'landing.testimonials.3.name': 'Wei C.',
    'landing.testimonials.3.role': 'Cross-Market Analyst',

    // Landing Page - CTA
    'landing.cta.title': 'Ready to Trade Smarter?',
    'landing.cta.subtitle': 'Join Sorane and get AI-powered insights for your next trade.',
    'landing.cta.button': 'Get Started — It\'s Free',

    // Landing Page - Footer
    'landing.footer.contact': 'Contact Us',
    'landing.footer.copyright': '© {year} Sorane. All rights reserved.',

    // Guest Wall
    'guest.signUpToUnlock': 'Sign up to see full results',
    'guest.freeAccount': 'Create a free account to unlock all stock analysis, recommendations, and more.',
    'guest.signUp': 'Sign Up Free',
    'guest.signIn': 'Sign In',
    'guest.previewNote': 'Showing preview — sign up for full access',

  },
  zh: {
    // Layout
    'nav.dashboard': '仪表盘',
    'nav.stockAnalysis': '股票分析',
    'nav.recommendations': '推荐选股',
    'nav.logout': '退出登录',
    'nav.login': '登录',
    'nav.loginHint': '更高请求频率',

    // Login
    'login.title': 'Sorane',
    'login.subtitle': 'AI驱动的智能交易平台',
    'login.email': '邮箱',
    'login.password': '密码',
    'login.submit': '登录',
    'login.loading': '登录中...',
    'login.noAccount': '还没有账号？',
    'login.register': '注册',
    'login.continueAsGuest': '以访客身份继续（有限访问）',
    'login.trustDevice': '信任此设备（保持登录 30 天）',

    // Register
    'register.title': '创建账号',
    'register.subtitle': '加入Sorane智能交易平台',
    'register.name': '姓名',
    'register.email': '邮箱',
    'register.password': '密码',
    'register.confirmPassword': '确认密码',
    'register.submit': '注册',
    'register.loading': '创建中...',
    'register.hasAccount': '已有账号？',
    'register.signIn': '登录',
    'register.passwordMismatch': '两次密码不一致',
    'register.passwordTooShort': '密码至少需要6个字符',
    'register.codeSent': '验证码已发送至您的邮箱。',
    'register.code': '验证码',
    'register.verify': '验证并创建账号',
    'register.verifying': '验证中...',
    'register.resend': '重新发送',
    'register.backToForm': '返回',

    // Dashboard
    'dash.title': '仪表盘',
    'dash.subtitle': 'AI驱动的股票分析与交易建议',
    'dash.stockAnalysis': '股票分析',
    'dash.stockAnalysisDesc': '基于评分系统的深度分析',
    'dash.recommendations': '推荐选股',
    'dash.recommendationsDesc': '月度股票筛选（美股 & A股）',
    'dash.contactTitle': '联系我们',
    'dash.contactBody': '如果您有任何问题或想法，请发送邮件至',
    'dash.version': '版本',
    'dash.placeholder': '输入股票代码（如 AAPL, NVDA, SH600000）',
    'dash.evaluate': '分析',
    'dash.analyzing': '分析中...',
    'dash.quick': '快捷:',

    // Market Emotion
    'emotion.title': '市场情绪',
    'emotion.usMarket': '美股',
    'emotion.cnMarket': 'A股',
    'emotion.score': '评分',
    'emotion.momentum': '市场动量',
    'emotion.breadth': '市场广度',
    'emotion.volatility': '波动率',
    'emotion.safeHaven': '避险需求',
    'emotion.junkSpread': '垃圾债利差',
    'emotion.rsi': '指数RSI',
    'emotion.highLow': '52周新高/新低',
    'emotion.loading': '正在读取市场脉搏...',
    'emotion.error': '无法加载市场情绪',

    // Stock Detail
    'stock.title': '股票分析',
    'stock.subtitle': '基于规则的股票评分，含价格走势与财务数据',
    'stock.placeholder': '输入股票代码（如 AAPL, NVDA, SH600000）',
    'stock.analyze': '分析',
    'stock.analyzing': '分析中...',
    'stock.quick': '快捷:',
    'stock.target': '目标价',
    'stock.overallScore': '综合评分',
    'stock.tabScores': '评分明细',
    'stock.tabChart': '价格走势',
    'stock.tabFinancials': '财务数据',
    'stock.scoreDimensions': '评分维度',
    'stock.keyMetrics': '关键指标',
    'stock.currentPrice': '当前价格',
    'stock.targetPrice': '目标价格',
    'stock.upside': '上涨空间',
    'stock.confidence': '置信度',
    'stock.sector': '行业',
    'stock.capCategory': '市值类别',
    'stock.strengths': '优势',
    'stock.risks': '风险',
    'stock.analysisSummary': '分析摘要',
    'stock.fiveDayTrend': '近5日趋势',
    'stock.oneMonthTrend': '近1月趋势',
    'stock.threeMonthTrend': '近3月趋势',
    'stock.peValuation': '市盈率估值',
    'stock.dividendYield': '股息率',
    'stock.betaRisk': 'Beta（风险）',
    'stock.marketCap': '市值',
    'stock.volAdjReturn': '风险调整收益',
    'stock.rsiSignal': 'RSI信号',
    'stock.earningsGrowth': '盈利增长',
    'stock.financialHealth': '财务健康度',
    'stock.sectorMomentum': '板块动量',
    'stock.noPriceData': '暂无价格数据',
    'stock.noFinancialData': '暂无财务数据',
    'stock.invalidTicker': '请输入有效的股票代码（如 AAPL）或A股代码（如 SH600519、002713）。不支持输入中文股票名称。',

    // Financials
    'fin.price': '价格',
    'fin.from52wHigh': '距52周高点',
    'fin.ytdReturn': '年初至今收益',
    'fin.peRatio': '市盈率',
    'fin.financialHealth': '财务健康度',
    'fin.strong': '强',
    'fin.moderate': '中等',
    'fin.weak': '弱',
    'fin.valuation': '估值',
    'fin.earningsProfitability': '盈利与利润',
    'fin.latestEps': '最新季度EPS',
    'fin.epsGrowth': 'EPS环比增长',
    'fin.latestRevenue': '最新营收',
    'fin.profitMargin': '利润率',
    'fin.freeCashFlow': '自由现金流',
    'fin.currentRatio': '流动比率',
    'fin.debtAssets': '负债/资产比',
    'fin.trailingPE': '滚动市盈率',
    'fin.forwardPE': '预期市盈率',
    'fin.priceBook': '市净率',
    'fin.pegRatio': 'PEG比率',
    'fin.evEbitda': 'EV/EBITDA',
    'fin.weekRange': '52周价格区间',
    'fin.perfVsMarket': '相对大盘表现',

    // Recommendations
    'rec.title': '推荐选股',
    'rec.subtitle': '基于趋势的股票筛选，覆盖美股与A股市场',
    'rec.usMarket': '🇺🇸 美股',
    'rec.cnMarket': '🇨🇳 A股',
    'rec.generate': '生成推荐',
    'rec.generating': '生成中...',
    'rec.marketHealth': '📊 市场整体健康度',
    'rec.stocksScored': '评分股票数',
    'rec.buySignals': '买入信号',
    'rec.sectorsWithPicks': '有推荐的行业',
    'rec.benchmarkRsi': '基准 RSI',
    'rec.topSectors': '表现最佳行业',
    'rec.topPicks': '综合精选',
    'rec.top': '首选',
    'rec.ticker': '代码',
    'rec.signal': '信号',
    'rec.score': '评分',
    'rec.price': '价格',
    'rec.ret1m': '近1月涨幅',
    'rec.rsi': 'RSI',
    'rec.sector': '行业',

    'landing.nav.features': '功能特色',
    'landing.nav.testimonials': '用户评价',
    'landing.nav.signup': '注册',
    'landing.nav.login': '登录',

    // Landing Page - Hero
    'landing.hero.title': 'AI驱动的智能交易分析',
    'landing.hero.subtitle': '智能股票评分、自定义策略、实时市场洞察——尽在一个平台。',
    'landing.hero.getStarted': '立即开始',
    'landing.hero.login': '登录',

    // Landing Page - Features
    'landing.features.title': '交易所需的一切工具',
    'landing.features.stockAnalysis.title': '股票分析',
    'landing.features.stockAnalysis.desc': '基于基本面和技术面的多维度深度评分分析。',
    'landing.features.aiRecommendations.title': 'AI推荐选股',
    'landing.features.aiRecommendations.desc': '基于规则评分的美股和A股月度筛选推荐。',
    'landing.features.marketEmotion.title': '市场情绪',
    'landing.features.marketEmotion.desc': '基于7大市场信号的实时恐惧与贪婪指数，覆盖美股和A股。',

    // Landing Page - Testimonials
    'landing.testimonials.title': '深受交易者信赖',
    'landing.testimonials.1.quote': 'Sorane的评分系统帮我发现了那些容易被忽略的低估股票。',
    'landing.testimonials.1.name': 'Alex T.',
    'landing.testimonials.1.role': '个人投资者',
    'landing.testimonials.2.quote': '策略实验室让我在投入资金前，用真实历史数据验证我的想法。',
    'landing.testimonials.2.name': 'Sarah L.',
    'landing.testimonials.2.role': '波段交易者',
    'landing.testimonials.3.quote': '一个平台同时覆盖美股和A股市场，这太方便了。',
    'landing.testimonials.3.name': 'Wei C.',
    'landing.testimonials.3.role': '跨市场分析师',

    // Landing Page - CTA
    'landing.cta.title': '准备好更聪明地交易了吗？',
    'landing.cta.subtitle': '加入Sorane，获取AI驱动的交易洞察。',
    'landing.cta.button': '立即开始 — 免费注册',

    // Landing Page - Footer
    'landing.footer.contact': '联系我们',
    'landing.footer.copyright': '© {year} Sorane. 保留所有权利。',

    // Guest Wall
    'guest.signUpToUnlock': '注册查看完整结果',
    'guest.freeAccount': '创建免费账号，解锁全部股票分析、推荐选股等功能。',
    'guest.signUp': '免费注册',
    'guest.signIn': '登录',
    'guest.previewNote': '仅显示预览 — 注册获取完整访问',

  },
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('sorane_lang') || 'en');

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === 'en' ? 'zh' : 'en';
      localStorage.setItem('sorane_lang', next);
      return next;
    });
  }, []);

  const t = useCallback((key) => translations[lang]?.[key] || translations.en[key] || key, [lang]);

  return (
    <I18nContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
